/**
 * api 交易对白名单按钮及弹框
 */
import './index.less';

import { Button, TextLink } from '@bfe/moly';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import getSymbols from '@bfe/trade-base-symbol-list';
import { useToggle } from 'ahooks';
import { Tabs } from 'antd';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';

import { ReactComponent as IconAlertCircle } from '@/assets/icon-alert-circle.svg';
import ByModal from '@/components/ByModal';
import { useErrorMsg } from '@/hooks/useErrorMsg';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import {
  getContractSymbolList,
  getLimitSymbol,
  updateLimitSymbol,
} from '@/services/apiManagement';
import { useUserStore } from '@/store/useUser';
import { complianceHide } from '@/utils/complianceHelpers';

import SymbolList from './comps/SymbolList';

interface IApiSymbolModal {
  visible: boolean;
  apikey: string;
  onCancel: () => void;
}
export const ApiSymbolModal: FC<IApiSymbolModal> = ({
  visible,
  apikey,
  onCancel,
}) => {
  const [$t, lang] = useI18nContext();
  const { mutate } = useUserProfile();
  const { msgError } = useErrorMsg(mutate);

  const [allSymbols, setAllSymbols] = useState<any[]>([]);
  const [spotSelfSymbols, setSpotSelfSymbols] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState('spot');
  const [contractSelfSymbols, setContractSelfSymbols] = useState<string[]>([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);

  const { complianceConfig } = useUserStore();

  // 显示弹窗时默认显示现货的交易对
  useEffect(() => {
    if (visible) {
      setActiveTabKey('spot');
      getSpotSymbolList();
      // 请求该API的交易对白名单
      apikey && getSelfSymbols(apikey);
    }
  }, [visible, apikey]);

  // 获取现货交易对
  const getSpotSymbolList = () => {
    setLoadingSymbols(true);

    // 接口当前传入的语言，此参数为必填项，虽然有默认值: localStorage.getItem('BYBIT_LANG_KEY') || 'en'，但如果不传，可能导致接口返回的数据由于语言的不同而不准确，重要!重要!重要!
    getSymbols(lang || 'en')
      .then((result: any) => {
        if (result?.symbols) {
          initSymbols(result?.symbols);
        } else {
          msgError(result);
        }
      })
      .finally(() => {
        setLoadingSymbols(false);
      });
  };

  // 获取衍生品交易对
  const getContractSymbols = () => {
    setLoadingSymbols(true);
    getContractSymbolList()
      .then((res: any) => {
        const contractKey = [
          'InverseFutures',
          'InversePerpetual',
          'LinearFutures',
          'LinearPerpetual',
          'UsdcFutures',
          'UsdcPerpetual',
        ];
        const symbols: any[] = [];
        if (res?.result) {
          Object.keys(res.result || {})
            .filter((key) => contractKey.includes(key))
            .forEach((key) => {
              const newSymbols = res.result[key];
              console.log('api-management-contract-res', newSymbols);
              if (Array.isArray(newSymbols) && newSymbols.length) {
                symbols.push(
                  ...newSymbols.map((item) => ({
                    symbolName: item?.symbolName,
                    iconDayUrl: item?.lightIcon,
                    symbolId: item?.symbolName,
                  })),
                );
              }
            });
        }
        setAllSymbols(symbols);
      })
      .catch((err) => {
        msgError(err);
      })
      .finally(() => {
        setLoadingSymbols(false);
      });
  };

  const initSymbols = (arr: any[]) => {
    const res: any[] = [];
    arr.map((i: any) => {
      res.push({
        symbolName: i?.symbolName,
        iconDayUrl: i?.iconDayUrl,
        symbolId: i?.symbolId,
      });
    });
    setAllSymbols(res);
  };

  // 获取API交易对
  const getSelfSymbols = (apikey: string) => {
    getLimitSymbol({ apikey })
      .then((res: any) => {
        if (res?.result?.spot) {
          setSpotSelfSymbols(res?.result?.spot);
        }
        if (res?.result?.future) {
          setContractSelfSymbols(res?.result?.future);
        }
      })
      .catch((err: any) => {
        msgError(err);
      });
  };

  // 删除选中的交易对
  const onDelete = (removedTag: string) => {
    const selfSymbols =
      activeTabKey === 'spot' ? spotSelfSymbols : contractSelfSymbols;
    const newTags = selfSymbols.filter((tag: any) => tag !== removedTag);
    if (activeTabKey === 'spot') {
      setSpotSelfSymbols(newTags);
    } else {
      setContractSelfSymbols(newTags);
    }
  };

  // 变更选中的交易对
  const onChangeWhiteList = (e: string[]) => {
    if (activeTabKey === 'spot') {
      setSpotSelfSymbols(e);
    } else {
      setContractSelfSymbols(e);
    }
  };

  // 点击确认
  const onConfirmClick = () => {
    const params = {
      apikey: apikey,
      spot: spotSelfSymbols,
      future: contractSelfSymbols,
    };
    setLoading(true);
    updateLimitSymbol(params)
      .then((res: any) => {
        // 关闭弹框
        onClose(true);
        setLoading(false);
      })
      .catch((err: any) => {
        msgError(err);
        setLoading(false);
      });
  };

  // 点击取消或关闭
  const onClose = (success?: boolean) => {
    if (!success && loading) return;

    // setSearchInput('');
    // setSymbols([]);
    setAllSymbols([]);
    setSpotSelfSymbols([]);
    setContractSelfSymbols([]);
    setLoading(false);
    onCancel();
  };

  const onTabChange = (key: string) => {
    setActiveTabKey(key);
    if (key === 'spot') {
      getSpotSymbolList();
    } else {
      getContractSymbols();
    }
  };

  const Footer = () => {
    return (
      <div className="btn-container">
        <Button
          variant="primary"
          loading={loading}
          onClick={onConfirmClick}
          block
        >
          {$t('Confirm')}
        </Button>
        <Button onClick={() => onClose()} block variant="secondary">
          {$t('Cancel')}
        </Button>
      </div>
    );
  };

  return (
    <ByModal
      visible={visible}
      title={
        $t('symbolWhiteList') !== 'symbolWhiteList'
          ? $t('symbolWhiteList')
          : '交易对白名单'
      }
      ByFooter={<Footer />}
      onOK={onConfirmClick}
      onCancel={onClose}
      modalProps={{ destroyOnClose: true }}
    >
      <div className={classNames('api-symbol-modal-body-container')}>
        <Tabs activeKey={activeTabKey} onChange={onTabChange} size="small">
          <Tabs.TabPane
            tab={
              $t('spotAndOption SymbolWhiteList') !==
              'spotAndOption SymbolWhiteList'
                ? $t('spotAndOption SymbolWhiteList')
                : '现货及杠杆交易白名单'
            }
            key="spot"
          >
            <SymbolList
              activeTabKey={activeTabKey}
              selfSymbols={spotSelfSymbols}
              allSymbols={allSymbols}
              onDelete={onDelete}
              onChangeWhiteList={onChangeWhiteList}
              loadingSymbols={loadingSymbols}
            />
          </Tabs.TabPane>
          {!complianceHide(complianceConfig?.contractSymbolWhiteList) && (
            <Tabs.TabPane
              tab={
                $t('contractSymbolWhiteList') !== 'contractSymbolWhiteList'
                  ? $t('contractSymbolWhiteList')
                  : '衍生品白名单'
              }
              key="contract"
            >
              <div className={classNames('contract-white-list-tips')}>
                <IconAlertCircle />
                <span>{$t('contractSymbolWhiteListTips')}</span>
              </div>
              <SymbolList
                activeTabKey={activeTabKey}
                selfSymbols={contractSelfSymbols}
                allSymbols={allSymbols}
                onDelete={onDelete}
                onChangeWhiteList={onChangeWhiteList}
                loadingSymbols={loadingSymbols}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      </div>
    </ByModal>
  );
};
interface IApiSymbolProps {
  apikey: string;
  apiManagementId?: string;
  subAccountId?: string;
  isFromSubAccount?: boolean;
  refreshTable?: () => void;
  disabled?: boolean;
}
export const ApiSymbolBtn: FC<IApiSymbolProps> = (props) => {
  const [$t] = useI18nContext();
  const [showModal, { toggle }] = useToggle(false);
  const { apikey, disabled } = props;

  return (
    <>
      <TextLink
        variant="primary"
        size="normal"
        onClick={toggle}
        closeIcon
        style={{ width: 'max-content' }}
        disabled={disabled}
      >
        {$t('symbolWhiteList') !== 'symbolWhiteList'
          ? $t('symbolWhiteList')
          : '交易对白名单'}
      </TextLink>

      <ApiSymbolModal visible={showModal} apikey={apikey} onCancel={toggle} />
    </>
  );
};
